// frame.js
//
// The "planning frame" — the shared coordinate space the editing core works in.
//
// The whole reason the KML view can reuse ImageEditor's tools is that NOTHING in
// those tools cares about pixels specifically; they care about a LINEAR space
// with a CONSTANT scale. Line/Boundary/BoundaryManager/LineManager/EditModeManager
// operate on abstract [x, y]. The only scale-aware bit is Line.generateWaypoints,
// where `25 / gsd` means "25 cm expressed in frame units".
//
// A frame supplies exactly the four values GeoInfoReader derives from a GeoTIFF:
//     gsd (cm per unit) · utmZone · pixelToGeo(x,y) -> [E, N] · geoToPixel(E,N) -> [x, y]
// so both backdrops are interchangeable to everything downstream.
//
//   Orthophoto backdrop -> frame comes from the GeoTIFF's affine transform.
//   Satellite backdrop  -> frame is synthesised here from a lon/lat anchor.
//
// Axis convention matches a raster and the existing code: +x east, +y SOUTH
// (y grows downward), origin at the frame's top-left corner.
//
//   [ADR: docs/20260826_satellite_kml_planning_frame.html]

import { fromLonLat, toLonLat, zoneFor, bandFor, centralMeridian } from './utm.js';

const WGS84_A = 6378137.0;

export const DEFAULT_CM_PER_PIXEL = 2.0; // ~ a good drone orthomosaic

// Real orthomosaics run tens of thousands of pixels across (the Auburn crops in
// field_scripts go to ~42000), and the editor's on-screen constants -- stroke
// widths, point-handle sizes, hit radii -- are absolute source-pixel numbers
// tuned for that range. A frame far outside it makes those constants render
// proportionally wrong, so the unit count is what must stay bounded, not the
// ground extent. Growing the canvas therefore coarsens resolution rather than
// inflating the unit count.
export const TARGET_MAX_UNITS = 40000;

// Resolution (cm per unit) that keeps `extentMeters` inside TARGET_MAX_UNITS.
// Never finer than `floorCm`, because sub-centimetre units buy nothing when the
// satellite imagery underneath is ~20 cm/px.
export function autoResolution(extentMeters, floorCm = DEFAULT_CM_PER_PIXEL) {
  return Math.max(floorCm, (extentMeters * 100) / TARGET_MAX_UNITS);
}

// Rough tile count a frame will request, so the UI can warn before firing
// thousands of requests at the tile server. Mirrors TileBackdrop's grid.
export function estimateTileCount(extentMetersW, extentMetersH, lat, zoom = 19) {
  const mPerTile = (156543.03392 * Math.cos((lat * Math.PI) / 180) / 2 ** zoom) * 256;
  return Math.ceil(extentMetersW / mPerTile + 1) * Math.ceil(extentMetersH / mPerTile + 1);
}

// Build a frame from a lon/lat anchor and a ground extent in metres.
//
// AXIS ALIGNMENT — the subtle part. The frame is aligned to TRUE north, not to
// UTM grid north. Those differ by the meridian convergence (~0.47 deg at this
// site, ~1.9 m across a single z17 tile). Satellite tiles are Web Mercator, so
// their edges are true-north meridians; a grid-north frame therefore receives
// every tile slightly ROTATED, and laying them out as axis-aligned rectangles
// shears the imagery and opens visible seams between tiles.
//
// Aligning the frame to true north removes that at the root: tiles become
// axis-aligned by construction, so they abut exactly. UTM is still what leaves
// the frame -- pixelToGeo projects through lon/lat -- so the export contract is
// unchanged; the rotation simply lives inside the projection instead of being
// wrongly ignored in the tile layout.
//
// Within a frame the mapping is equirectangular about the anchor latitude. The
// residual against Mercator's non-linear latitude is under a centimetre over a
// tile, far below the 1.9 m error it replaces.
const WGS84_E2 = 0.00669437999014;
function radiiAt(latDeg) {
  const s2 = Math.sin((latDeg * Math.PI) / 180) ** 2;
  const w = 1 - WGS84_E2 * s2;
  return {
    M: (WGS84_A * (1 - WGS84_E2)) / Math.pow(w, 1.5),  // meridional (north-south)
    N: WGS84_A / Math.sqrt(w),                          // normal (east-west)
  };
}

export function createFrame({
  centerLon, centerLat,
  widthMeters = 800, heightMeters = 800,
  cmPerPixel = DEFAULT_CM_PER_PIXEL,
}) {
  const zone = zoneFor(centerLon, centerLat);
  const band = bandFor(centerLat);
  const northern = centerLat >= 0;

  const mPerUnit = cmPerPixel / 100;
  const width = Math.round(widthMeters / mPerUnit);
  const height = Math.round(heightMeters / mPerUnit);

  const { M, N } = radiiAt(centerLat);
  const degLatPerM = 180 / (Math.PI * M);
  const degLonPerM = 180 / (Math.PI * N * Math.cos((centerLat * Math.PI) / 180));

  // Top-left (north-west) corner of the frame, in lon/lat.
  const originLat = centerLat + (heightMeters / 2) * degLatPerM;
  const originLon = centerLon - (widthMeters / 2) * degLonPerM;

  const pixelToLonLat = (x, y) => ({
    lon: originLon + x * mPerUnit * degLonPerM,
    lat: originLat - y * mPerUnit * degLatPerM,
  });
  const lonLatToPixel = (lon, lat) => [
    Math.round((lon - originLon) / degLonPerM / mPerUnit),
    Math.round((originLat - lat) / degLatPerM / mPerUnit),
  ];

  // The editor's contract: pixels in, UTM easting/northing out.
  const pixelToGeo = (x, y) => {
    const { lon, lat } = pixelToLonLat(x, y);
    const u = fromLonLat(lon, lat, zone);
    return [u.easting, u.northing];
  };
  const geoToPixel = (e, n) => {
    const { lon, lat } = toLonLat(e, n, zone, northern);
    return lonLatToPixel(lon, lat);
  };

  return {
    kind: 'synthetic',
    gsd: cmPerPixel,               // cm per unit — same units GeoInfoReader reports
    utmZone: `${zone}${band || ''}`,
    zone, band, northern,
    width, height, mPerUnit,
    originLon, originLat,
    centerLon, centerLat,
    pixelToGeo,
    geoToPixel,
    lonLatToPixel,
    pixelToLonLat,
    // True north vs UTM grid north, for reporting bearings honestly.
    convergenceDeg: (() => {
      const dl = ((centerLon - centralMeridian(zone)) * Math.PI) / 180;
      return (Math.atan(Math.tan(dl) * Math.sin((centerLat * Math.PI) / 180)) * 180) / Math.PI;
    })(),
  };
}

// Frame sized to a KML's lon/lat bounding box, with margin so the operator has
// room to draw headlands OUTSIDE the plot — which this site actually needs,
// since a 13.7 m strip cannot contain a ~10 m turn.
export function createFrameFromBounds(bounds, {
  cmPerPixel = DEFAULT_CM_PER_PIXEL,
  marginMeters = 150,
  minExtentMeters = 200,
} = {}) {
  const { minLon, minLat, maxLon, maxLat } = bounds;
  const centerLon = (minLon + maxLon) / 2;
  const centerLat = (minLat + maxLat) / 2;

  // Ground size of the bbox, via UTM so it is metric and not degree-skewed.
  const zone = zoneFor(centerLon, centerLat);
  const a = fromLonLat(minLon, minLat, zone);
  const b = fromLonLat(maxLon, maxLat, zone);
  const wM = Math.max(Math.abs(b.easting - a.easting), 1);
  const hM = Math.max(Math.abs(b.northing - a.northing), 1);

  return createFrame({
    centerLon,
    centerLat,
    widthMeters: Math.max(wM + 2 * marginMeters, minExtentMeters),
    heightMeters: Math.max(hM + 2 * marginMeters, minExtentMeters),
    cmPerPixel,
  });
}

// Wrap the GeoTIFF-derived transforms in the same shape, so consumers never
// branch on which backdrop they are running over.
export function createFrameFromGeoTiff({ gsd, utmZone, pixelToGeo, geoToPixel, width, height }) {
  return {
    kind: 'geotiff',
    gsd, utmZone, width, height,
    mPerUnit: gsd / 100,
    pixelToGeo, geoToPixel,
    lonLatToPixel: null,  // not needed: the raster IS the reference
    pixelToLonLat: null,
  };
}
